import { Injectable } from '@angular/core';
import {
  getValue,
  isDefinedAndFilledArray,
} from '../../../../shared/utility/verify.lib';
import { IExportConfiguration } from '../../../../models/export/export-configuration.model';
import { IDocumentSession } from '../../../../models/session/document-session.model';
import { cloneDeep, isEqual } from 'lodash';
import { IExportStructure } from '../../../../models/export/export-structure.model';
import { ExportSettingType } from '../../enum/export-setting-type.enum';
import { SessionType } from '../../enum/session-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ExportStructureHelperService {
  public didShortNamesChange(
    defaultConfig: IExportConfiguration,
    savedConfig: IExportConfiguration,
    documentSession: IDocumentSession,
    needFiles: boolean,
    needFolders: boolean
  ): boolean {
    let returnValue: boolean = false;
    const defaultShortNames: string[] = this.getShortNames(
      getValue(() => defaultConfig.fileSetting, []),
      documentSession,
      needFiles
    ).concat(
      this.getShortNames(
        getValue(() => defaultConfig.folderSetting, []),
        documentSession,
        needFolders
      )
    );
    const savedShortNames: string[] = this.getShortNames(
      getValue(() => savedConfig.fileSetting, []),
      documentSession,
      needFiles
    ).concat(
      this.getShortNames(
        getValue(() => savedConfig.folderSetting, []),
        documentSession,
        needFolders
      )
    );

    if (isDefinedAndFilledArray(() => savedShortNames)) {
      returnValue = !isEqual(defaultShortNames, savedShortNames);
    }

    return returnValue;
  }

  public getMappedShortName(
    settingStructure: IExportStructure,
    documentSession: IDocumentSession
  ): ExportSettingType {
    return this.mapQuoteAndOrder(
      getValue(() => settingStructure.shortName),
      documentSession
    );
  }

  public getShortNames(
    settings: IExportStructure[],
    documentSession: IDocumentSession,
    isNeeded: boolean = true
  ): string[] {
    return isNeeded && isDefinedAndFilledArray(() => settings)
      ? settings.map((setting: IExportStructure) =>
          this.mapQuoteAndOrder(
            getValue(() => setting.shortName, ''),
            documentSession
          )
        )
      : [];
  }

  public updatePreselectedInDropdown(
    settings: IExportStructure[],
    documentSession: IDocumentSession,
    preselectedInDropdown: string[]
  ): string[] {
    let returnValue: string[] = cloneDeep(preselectedInDropdown);

    if (isDefinedAndFilledArray(() => settings)) {
      settings.forEach((settingStructure: IExportStructure, index: number) => {
        if (index < getValue(() => preselectedInDropdown.length, 0)) {
          returnValue[index] = this.getMappedShortName(
            settingStructure,
            documentSession
          );
        }
      });
    }

    return returnValue;
  }

  public filterAvailableStructure(
    structures: IExportStructure[],
    availableShortNames: string[],
    documentSession: IDocumentSession
  ): IExportStructure[] {
    let returnValue: IExportStructure[] = [];
    structures.forEach((setting: IExportStructure) => {
      const currentShortName: string = this.getMappedShortName(
        setting,
        documentSession
      );
      if (availableShortNames.includes(currentShortName)) {
        returnValue.push(setting);
      } else {
        returnValue.push(null);
      }
    });

    return returnValue;
  }

  private mapQuoteAndOrder(
    settingsType: ExportSettingType,
    documentSession: IDocumentSession
  ): ExportSettingType {
    let returnValue: ExportSettingType = settingsType;

    if (documentSession.type === SessionType.QUOTE) {
      switch (settingsType) {
        case ExportSettingType.ORDER_NUMBER:
          returnValue = ExportSettingType.QUOTE_NUMBER;
          break;
        case ExportSettingType.ORDER_POSITION_NUMBER:
          returnValue = ExportSettingType.QUOTE_POSITION_NUMBER;
          break;
        case ExportSettingType.ORDER_REFERENCE:
          returnValue = ExportSettingType.QUOTE_REFERENCE;
          break;
      }
    } else {
      switch (settingsType) {
        case ExportSettingType.QUOTE_NUMBER:
          returnValue = ExportSettingType.ORDER_NUMBER;
          break;
        case ExportSettingType.QUOTE_POSITION_NUMBER:
          returnValue = ExportSettingType.ORDER_POSITION_NUMBER;
          break;
        case ExportSettingType.QUOTE_REFERENCE:
          returnValue = ExportSettingType.ORDER_REFERENCE;
          break;
      }
    }

    return returnValue;
  }
}
